import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withSnackbar } from 'notistack';
import {
	setAlertActionCreator,
	setPageHeaderTextActionCreator,
	setTableStateActionCreator,
} from 'plugins/store/reducers/common.reducer';
import { setNumberRowsPerPageActionCreator } from 'plugins/store/reducers/settings.reducer';
import {
	searchContactCenters,
	deleteContactCentersList,
} from 'services/contact-center.service';
import { mapContactCentersResult } from 'mappings/contactCentersMappings';
import { can } from 'utils/permissions/permissionsChecker';
import permissionsMap from 'utils/permissions/permissionsMap';
import layoutNames from 'utils/enums/layout-names.enum';

// @material-ui/icons
import BusinessOutlined from '@material-ui/icons/BusinessOutlined';
import AddContactCenterIcon from 'assets/img/add-contact-center.svg';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import ReactTable from 'components/ReactTable/ReactTable';
import SnackMessage from 'components/Snackbar/SnackMessage';
import Tooltip from '@material-ui/core/Tooltip';

import { withStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/contactCentersStyle';

class ContactCenters extends Component {
	state = {
		data: [],
		totalRecordsCount: 0,
		pageCount: 0,
		loading: true,
	};

	viewName = 'Contact Centers';

	columns = [
		{
			Header: 'Name',
			accessor: 'name',
		},
	];

	componentDidMount() {
		this.props.setPageHeaderText(this.viewName);
	}

	componentWillUnmount() {
		this.props.setPageHeaderText('');
	}

	createCenter = () => {
		this.props.history.push({ pathname: 'contact-centers/create' });
	}

	render() {
		const {
			setAlert,
			classes,
			rowsPerPage,
			setRowsPerPage,
			defaultSearchQuery,
			defaultSortBy,
			defaultPageIndex,
			setTableState,
		} = this.props;

		this.fetchData = async ({
			filterQuery,
			pageIndex,
			pageSize,
			sortBy,
		}) => {
			this.setState({ loading: true });
			const result = await searchContactCenters({
				filterQuery,
				pageIndex,
				pageSize,
				sortBy,
			});
			const mappedResult = mapContactCentersResult(result);
			this.setState({
				data: mappedResult.data,
				totalRecordsCount: mappedResult.total,
				pageCount: Math.ceil(mappedResult.total / pageSize),
				loading: false,
			});
			setRowsPerPage({
				table: 'contactCenters',
				rowsPerPage: pageSize
			});
			setTableState({
				table: 'contactCenters',
				state: {
					searchQuery: filterQuery,
					sortBy: sortBy,
					page: pageIndex
				}
			});
		};

		this.onDelete = (selection, refreshData) => {
			const { enqueueSnackbar } = this.props;
			const idsToDelete = selection.map((item) => {
				return item.original.id;
			});
			const deleteNotifications = {
				type: 'warning',
				title: 'Do you want to remove selected Contact Center(s)?',
				message: 'This action can not be undone',
				confirmBtnText: 'REMOVE',
				onConfirm: async () => {
					setAlert(null);
					try {
						await deleteContactCentersList(idsToDelete);
						refreshData();
						enqueueSnackbar(
							'Contact Center(s) successfully removed',
							{
								content: (key, message) => (
									<SnackMessage
										id={key}
										message={message}
										color="success"
									/>
								),
							}
						);
					} catch (e) {
						enqueueSnackbar(e.resolvedErrorMessage, {
							content: (key, message) => (
								<SnackMessage
									id={key}
									message={message}
									color="danger"
								/>
							),
						});
					}
				},
				onCancel: () => {
					setAlert(null);
				},
			};
			setAlert(deleteNotifications);
		};

		return (
			<GridContainer>
				<GridItem xs={12} sm={12} md={12}>
					<Card>
						<CardHeader style={styles.cardHeader}  color="primary" icon>
							<CardIcon color="primary">
								<BusinessOutlined />
							</CardIcon>
						</CardHeader>
						<CardBody>
							<GridContainer>
								<GridItem xs={12} sm={12} md={12}>
									<ReactTable
										rowsWithSelection={can(permissionsMap.contactCenter.items.contactCenter.delete)}
										isAllowDelete={can(permissionsMap.contactCenter.items.contactCenter.delete)}
										columns={this.columns}
										data={this.state.data}
										fetchData={this.fetchData}
										rowsPerPage={rowsPerPage}
										defaultSearchQuery={defaultSearchQuery}
										defaultSortBy={defaultSortBy}
										defaultPageIndex={defaultPageIndex}
										loading={this.state.loading}
										pageCount={this.state.pageCount}
										totalRecordsCount={
											this.state.totalRecordsCount
										}
										detailsPath={
											`${layoutNames.portal}/contact-centers/details`
										}
										addRecordComponent={
											can(permissionsMap.contactCenter.items.contactCenter.create) &&
											(
												<div className={classes.right}>
													<Tooltip
														arrow
														title="Create Contact Center"
													>
														<img
															onClick={this.createCenter}
															src={AddContactCenterIcon}
															alt="add contact center button"
															className={classes.addRecord}
														/>
													</Tooltip>
												</div>
											)
										}
										onDelete={this.onDelete}
										isPageRootComponent
									/>
								</GridItem>
							</GridContainer>
						</CardBody>
					</Card>
				</GridItem>
			</GridContainer>
		);
	}
}

ContactCenters.propTypes = {
	classes: PropTypes.any,
	setPageHeaderText: PropTypes.func,
	setAlert: PropTypes.func,
	location: PropTypes.object,
};

const mapStateToProps = ({
	common: {
		tablesState: {
			contactCenters: {
				searchQuery,
				sortBy,
				page,
			} }
	},
	settings: { rowsPerPage } }) => {
	return {
		rowsPerPage: rowsPerPage.contactCenters,
		defaultSearchQuery: searchQuery,
		defaultSortBy: sortBy,
		defaultPageIndex: page,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(setAlertActionCreator, dispatch),
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch
	),
	setRowsPerPage: bindActionCreators(
		setNumberRowsPerPageActionCreator,
		dispatch
	),
	setTableState: bindActionCreators(
		setTableStateActionCreator,
		dispatch
	),
});

export default withStyles(styles)(
	connect(mapStateToProps, mapDispatchToProps)(withSnackbar(ContactCenters))
);
